import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Trash2, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminFAQsPage = () => {
  const { token, hasRole } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [form, setForm] = useState({ question: '', answer: '', category: 'Booking' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchFAQs(); }, []);

  const fetchFAQs = async () => {
    try {
      const res = await fetch(`${API}/admin/faqs`, { headers: { 'Authorization': `Bearer ${token}` }});
      if (res.ok) setFaqs(await res.json());
    } catch (error) { console.error('Failed:', error); }
    finally { setLoading(false); }
  };

  const createFAQ = async () => {
    try {
      const res = await fetch(`${API}/admin/faqs`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) { toast.success('FAQ created'); setShowModal(false); setForm({ question: '', answer: '', category: 'Booking' }); fetchFAQs(); }
    } catch (error) { toast.error('Failed'); }
  };

  const deleteFAQ = async (faqId) => {
    if (!confirm('Delete this FAQ?')) return;
    try {
      await fetch(`${API}/admin/faqs/${faqId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }});
      toast.success('FAQ deleted'); fetchFAQs();
    } catch (error) { toast.error('Failed'); }
  };

  const categories = [...new Set(faqs.map(f => f.category))];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>FAQs</h1>
            <p className={`${theme.textMuted} mt-1`}>Manage frequently asked questions</p>
          </div>
          {hasRole(['super_admin', 'manager']) && (
            <Button onClick={() => setShowModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" /> Add FAQ
            </Button>
          )}
        </div>

        {loading ? (
          <div className={`animate-spin rounded-full h-8 w-8 border-b-2 mx-auto ${isDarkMode ? 'border-emerald-500' : 'border-emerald-600'}`}></div>
        ) : faqs.length === 0 ? (
          <Card className={theme.card}>
            <CardContent className="py-12 text-center">
              <HelpCircle className={`h-12 w-12 mx-auto mb-4 ${theme.textMuted}`} />
              <p className={theme.textMuted}>No FAQs yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {categories.map((category) => (
              <Card key={category} className={theme.card}>
                <CardHeader><CardTitle className={`text-lg ${theme.text}`}>{category}</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {faqs.filter(f => f.category === category).map((faq) => (
                    <div key={faq.faq_id} className={`rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                      <button
                        className="w-full p-4 flex items-center justify-between text-left"
                        onClick={() => setExpandedFaq(expandedFaq === faq.faq_id ? null : faq.faq_id)}
                      >
                        <span className={`font-medium ${theme.text}`}>{faq.question}</span>
                        <div className="flex items-center gap-2">
                          {hasRole(['super_admin', 'manager']) && (
                            <Button size="sm" variant="ghost" className="text-rose-400 hover:text-rose-300" onClick={(e) => { e.stopPropagation(); deleteFAQ(faq.faq_id); }}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                          {expandedFaq === faq.faq_id ? <ChevronUp className={`h-4 w-4 ${theme.textMuted}`} /> : <ChevronDown className={`h-4 w-4 ${theme.textMuted}`} />}
                        </div>
                      </button>
                      {expandedFaq === faq.faq_id && (
                        <div className={`px-4 pb-4 ${theme.textMuted}`}>{faq.answer}</div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-md ${theme.modalBg}`}>
              <CardHeader><CardTitle className={theme.text}>Add FAQ</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className={theme.text}>Question</Label>
                  <Input value={form.question} onChange={(e) => setForm(p => ({ ...p, question: e.target.value }))} className={theme.input} />
                </div>
                <div>
                  <Label className={theme.text}>Answer</Label>
                  <textarea value={form.answer} onChange={(e) => setForm(p => ({ ...p, answer: e.target.value }))} className={`w-full h-24 px-3 py-2 rounded-md border resize-none ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} />
                </div>
                <div>
                  <Label className={theme.text}>Category</Label>
                  <select value={form.category} onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))} className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                    <option>Booking</option><option>Membership</option><option>Open Play</option><option>Equipment</option><option>Rewards</option><option>General</option>
                  </select>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" className={`flex-1 ${theme.btnOutline}`} onClick={() => setShowModal(false)}>Cancel</Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={createFAQ}>Create</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminFAQsPage;
